import { graphqlApiWithPersisted } from './graphqlApi';

export enum UserNotificationActivityKind {
  COMMENT_ON_MY_CARD = 'commentOnMyCard',
  COMMENT_ON_MY_COMMENT = 'commentOnMyComment',
  REACTION_ON_MY_COMMENT = 'reactionOnMyComment',
  MENTION_ME_ON_CARD = 'mentionMeOnCard',
}

export enum UserNotificationAchievementKind {
  CLOSE_TO_NEW_BADGE = 'achievementCloseToNewBadge',
  EARN_NEW_BADGE = 'achievementEarnNewBadge',
}

export type UserNotificationPreferenceKind =
  | UserNotificationActivityKind
  | UserNotificationAchievementKind;

export interface NotificationPreferencePush {
  push: boolean;
}

export interface NotificationPreferenceEmail extends NotificationPreferencePush {
  email: boolean;
}

export interface UserNotificationPreference {
  achievementCloseToNewBadge: NotificationPreferenceEmail;
  achievementEarnNewBadge: NotificationPreferenceEmail;
  commentOnMyCard: NotificationPreferencePush;
  commentOnMyComment: NotificationPreferencePush;
  mentionMeOnCard: NotificationPreferenceEmail;
  reactionOnMyComment: NotificationPreferencePush;
}

export interface NotificationPreferenceUpdateFields {
  push?: boolean;
  email?: boolean;
}

export interface UserNotificationPreferenceUpdateArgs {
  kind: UserNotificationPreferenceKind;
  fields: NotificationPreferenceUpdateFields;
}

export interface StoryNotificationPreference {
  id: number;
  title: string;
  subscriptionStatus: boolean;
}

export interface ChannelNotificationPreference {
  id: number;
  name: string;
  userNotificationPreference: { allowToReceiveChannelNotification: boolean };
  stories: StoryNotificationPreference[];
}

interface MeNotificationPreferenceResponse {
  data: { meNotificationPreference: UserNotificationPreference };
}

interface UserNotificationPreferenceUpdateResponse {
  data: { userNotificationPreferenceUpdate: { payload: UserNotificationPreference | null } };
}

interface ChannelsPreviewNotificationPreferencesResponse {
  data: { channelsPreviewNotificationPreferences: ChannelNotificationPreference[] };
}

const USER_NOTIFICATION_PREFERENCE_FIELDS = `
  achievementCloseToNewBadge { push email }
  achievementEarnNewBadge { push email }
  commentOnMyCard { push }
  commentOnMyComment { push }
  mentionMeOnCard { push email }
  reactionOnMyComment { push }
`;

export const notificationSettingsApi = graphqlApiWithPersisted.injectEndpoints({
  endpoints: (builder) => ({
    meNotificationPreference: builder.query<UserNotificationPreference, void>({
      query: () => ({
        url: '/graphql/webapp?meNotificationPreference',
        method: 'POST',
        body: {
          query: `query MeNotificationPreference {
            meNotificationPreference {
              ${USER_NOTIFICATION_PREFERENCE_FIELDS}
            }
          }`,
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: MeNotificationPreferenceResponse) =>
        response.data.meNotificationPreference,
    }),
    channelsPreviewNotificationPreferences: builder.query<ChannelNotificationPreference[], void>({
      query: () => ({
        url: '/graphql/webapp?channelsPreviewNotificationPreferences',
        method: 'POST',
        body: {
          query: `query ChannelsPreviewNotificationPreferences {
            channelsPreviewNotificationPreferences {
              id
              name
              userNotificationPreference {
                allowToReceiveChannelNotification
              }
              stories {
                id
                title
                subscriptionStatus
              }
            }
          }`,
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: ChannelsPreviewNotificationPreferencesResponse) =>
        response.data.channelsPreviewNotificationPreferences,
    }),
    userNotificationPreferenceUpdate: builder.mutation<
      UserNotificationPreference | null,
      UserNotificationPreferenceUpdateArgs
    >({
      query: ({ kind, fields }) => ({
        url: '/graphql/webapp?userNotificationPreferenceUpdate',
        method: 'POST',
        body: {
          query: `mutation UserNotificationPreferenceUpdate($input: UserNotificationPreferenceUpdateInput!) {
            userNotificationPreferenceUpdate(input: $input) {
              payload {
                ${USER_NOTIFICATION_PREFERENCE_FIELDS}
              }
              error {
                ... on UnknownError {
                  message
                  kind
                }
                ... on UserNotFoundError {
                  message
                }
                ... on UserNotificationPreferenceValidationError {
                  message
                }
              }
            }
          }`,
          variables: { input: { [kind]: fields } },
        },
      }),
      transformResponse: (response: UserNotificationPreferenceUpdateResponse) =>
        response.data.userNotificationPreferenceUpdate.payload,
    }),
  }),
});

export const {
  useChannelsPreviewNotificationPreferencesQuery,
  useMeNotificationPreferenceQuery,
  useUserNotificationPreferenceUpdateMutation,
} = notificationSettingsApi;
